// Naval Battleship: Fleet Strike Server Engine (2 Players + Spectators)
export class BattleshipEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.players = [];
    this.grids = {}; // id -> 10x10 array (0: empty, 1: ship, 2: hit, 3: miss)
    this.ships = {}; // id -> array of ship objects
    this.currentTurnIdx = 0;
    this.phase = 'DEPLOYMENT'; // 'DEPLOYMENT' | 'BATTLE'
  }

  start() {
    const shouldFillBots = this.room.settings?.fillBots !== false;
    this.players = this.room.players.slice(0, 2).map(p => ({
      id: p.id,
      name: p.name,
      isBot: !!p.isBot
    }));

    if (this.players.length === 1 && shouldFillBots) {
      this.players.push({ id: 'bot_admiral', name: 'Admiral Bot ⚓', isBot: true });
    }

    this.grids = {};
    this.ships = {};

    this.players.forEach((p) => {
      this.grids[p.id] = Array(10).fill(null).map(() => Array(10).fill(0));
      this.autoDeployFleet(p.id);
    });

    this.phase = 'BATTLE';
    this.currentTurnIdx = 0;

    this.players.forEach((p) => {
      if (!p.isBot) {
        this.io.to(p.id).emit('bs_started', {
          myGrid: this.grids[p.id],
          myShips: this.ships[p.id],
          currentTurnId: this.players[this.currentTurnIdx]?.id,
          players: this.players
        });
      }
    });

    this.checkBotTurn();
  }

  autoDeployFleet(playerId) {
    const shipDefs = [
      { name: 'Carrier', size: 5, hits: 0 },
      { name: 'Battleship', size: 4, hits: 0 },
      { name: 'Cruiser', size: 3, hits: 0 },
      { name: 'Submarine', size: 3, hits: 0 },
      { name: 'Destroyer', size: 2, hits: 0 }
    ];

    const grid = this.grids[playerId];
    const placedShips = [];

    shipDefs.forEach((s) => {
      let placed = false;
      while (!placed) {
        const isHoriz = Math.random() > 0.5;
        const r = Math.floor(Math.random() * (isHoriz ? 10 : 10 - s.size));
        const c = Math.floor(Math.random() * (isHoriz ? 10 - s.size : 10));

        let canPlace = true;
        const cells = [];
        for (let i = 0; i < s.size; i++) {
          const checkR = isHoriz ? r : r + i;
          const checkC = isHoriz ? c + i : c;
          if (grid[checkR][checkC] !== 0) { canPlace = false; break; }
          cells.push({ r: checkR, c: checkC });
        }

        if (canPlace) {
          cells.forEach(pt => { grid[pt.r][pt.c] = 1; });
          placedShips.push({ ...s, cells });
          placed = true;
        }
      }
    });

    this.ships[playerId] = placedShips;
  }

  checkBotTurn() {
    const cp = this.players[this.currentTurnIdx];
    if (cp && cp.isBot && this.phase === 'BATTLE') {
      setTimeout(() => {
        const targetPlayer = this.players.find(p => p.id !== cp.id);
        if (targetPlayer) {
          const enemyGrid = this.grids[targetPlayer.id];
          const unhit = [];
          for (let r = 0; r < 10; r++) {
            for (let c = 0; c < 10; c++) {
              if (enemyGrid[r][c] === 0 || enemyGrid[r][c] === 1) unhit.push({ r, c });
            }
          }
          if (unhit.length > 0) {
            const pick = unhit[Math.floor(Math.random() * unhit.length)];
            this.fireStrike(cp.id, pick.r, pick.c);
          }
        }
      }, 1500);
    }
  }

  fireStrike(attackerId, row, col) {
    const cp = this.players[this.currentTurnIdx];
    if (!cp || String(cp.id).toLowerCase() !== String(attackerId).toLowerCase() || this.phase !== 'BATTLE') return;

    const defender = this.players.find(p => String(p.id).toLowerCase() !== String(attackerId).toLowerCase());
    if (!defender) return;

    const defGrid = this.grids[defender.id];
    const cellVal = defGrid[row][col];

    if (cellVal === 2 || cellVal === 3) return; // Already targeted

    const isHit = cellVal === 1;
    defGrid[row][col] = isHit ? 2 : 3;

    let sunkShip = null;
    if (isHit) {
      const ship = this.ships[defender.id].find(s => s.cells.some(pt => pt.r === row && pt.c === col));
      if (ship) {
        ship.hits++;
        if (ship.hits >= ship.size) {
          sunkShip = ship.name;
        }
      }
    }

    this.io.to(this.room.code).emit('bs_strikeResult', {
      attackerId,
      defenderId: defender.id,
      row,
      col,
      isHit,
      sunkShip
    });

    // Check Win
    const allSunk = this.ships[defender.id].every(s => s.hits >= s.size);
    if (allSunk) {
      this.phase = 'OVER';
      this.io.to(this.room.code).emit('bs_gameOver', { winner: cp });
      return;
    }

    // Next Turn
    this.currentTurnIdx = (this.currentTurnIdx + 1) % this.players.length;
    this.io.to(this.room.code).emit('bs_turnChange', {
      currentTurnId: this.players[this.currentTurnIdx]?.id
    });

    this.checkBotTurn();
  }

  destroy() {}
}
